import { useEffect, useRef, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../store/slices/authSlice';
import toast from 'react-hot-toast';

// ── Constants ────────────────────────────────────────────────────────────────
const IDLE_TIMEOUT_MS   = 60 * 60 * 1000; // 60 minutes of inactivity → logout
const WARNING_BEFORE_MS =  2 * 60 * 1000; // show warning 2 min before logout

// Activity events that reset the idle timer
const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'click',
];

/**
 * useSessionTimeout
 *
 * Tracks user inactivity. After IDLE_TIMEOUT_MS (60 min) of no activity:
 *  - At (IDLE_TIMEOUT_MS - WARNING_BEFORE_MS): shows a warning modal with
 *    a live countdown giving the user a chance to stay logged in.
 *  - At IDLE_TIMEOUT_MS: automatically dispatches logout() and redirects to /login.
 *
 * Any user activity (mouse, keyboard, scroll, touch) resets the timer.
 *
 * @param {boolean} enabled  Pass false to disable (e.g. on the login page itself).
 * @returns {{ showWarning: boolean, secondsLeft: number, resetTimer: Function, performLogout: Function }}
 */
export function useSessionTimeout(enabled = true) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  // Refs for the three timers so we can clear them reliably across renders
  const idleTimerRef    = useRef(null); // fires the actual logout
  const warnTimerRef    = useRef(null); // fires the warning modal
  const countdownRef    = useRef(null); // 1-second countdown inside the warning
  const isWarningRef    = useRef(false); // true while warning is visible

  // ── Logout ──────────────────────────────────────────────────────────────
  const performLogout = useCallback(() => {
    clearTimeout(idleTimerRef.current);
    clearTimeout(warnTimerRef.current);
    clearInterval(countdownRef.current);
    isWarningRef.current = false;
    setShowWarning(false);
    dispatch(logout());
    navigate('/login', { replace: true });
    toast.error('You were logged out due to inactivity.');
  }, [dispatch, navigate]);

  // ── Reset / restart the idle timer ──────────────────────────────────────
  const resetTimer = useCallback(() => {
    if (!enabled) return;

    // Clear everything
    clearTimeout(idleTimerRef.current);
    clearTimeout(warnTimerRef.current);
    clearInterval(countdownRef.current);
    isWarningRef.current = false;
    setShowWarning(false);

    // Schedule the warning modal (58-minute mark, 2 min before logout)
    warnTimerRef.current = setTimeout(() => {
      isWarningRef.current = true;
      setShowWarning(true);

      // Start the countdown display
      let secs = Math.round(WARNING_BEFORE_MS / 1000);
      setSecondsLeft(secs);

      countdownRef.current = setInterval(() => {
        secs -= 1;
        setSecondsLeft(secs);
        if (secs <= 0) clearInterval(countdownRef.current);
      }, 1_000);
    }, IDLE_TIMEOUT_MS - WARNING_BEFORE_MS);

    // Schedule the auto-logout (60-minute mark)
    idleTimerRef.current = setTimeout(performLogout, IDLE_TIMEOUT_MS);
  }, [enabled, performLogout]);

  // ── Attach activity listeners ────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;

    const onActivity = () => resetTimer();

    ACTIVITY_EVENTS.forEach((ev) =>
      window.addEventListener(ev, onActivity, { passive: true })
    );

    resetTimer(); // kick off the initial timer on mount

    return () => {
      ACTIVITY_EVENTS.forEach((ev) =>
        window.removeEventListener(ev, onActivity)
      );
      clearTimeout(idleTimerRef.current);
      clearTimeout(warnTimerRef.current);
      clearInterval(countdownRef.current);
    };
  }, [enabled, resetTimer]);

  return { showWarning, secondsLeft, resetTimer, performLogout };
}
